// API service functions matching the original implementation

const API_BASE = '/api/v1'

/**
 * Send a message to the conversation API
 */
export async function sendMessage(token, payload) {
  const response = await fetch(`${API_BASE}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    // Parse error details from response body
    let errorData
    try {
      errorData = await response.json()
    } catch {
      errorData = { detail: `HTTP ${response.status}` }
    }

    const error = new Error(errorData.detail?.user_message || errorData.detail || `HTTP ${response.status}`)
    error.status = response.status
    error.details = errorData.detail
    throw error
  }

  return await response.json()
}

/**
 * Get messages for a conversation
 */
export async function getConversationMessages(token, conversationId) {
  const response = await fetch(`${API_BASE}/conversations/${conversationId}/messages`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return await response.json()
}

/**
 * List conversations with optional search
 */
export async function listConversations(token, searchQuery = '', limit = 50) {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (searchQuery) {
    params.append('search', searchQuery)
  }

  const response = await fetch(`${API_BASE}/conversations?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return await response.json()
}

/**
 * Update conversation (e.g., rename)
 */
export async function updateConversation(token, conversationId, updates) {
  const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(updates)
  })

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return await response.json()
}

/**
 * Delete a conversation
 */
export async function deleteConversation(token, conversationId) {
  const response = await fetch(`${API_BASE}/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return response.ok
}

/**
 * Get user profile
 */
export async function getProfile(token) {
  const response = await fetch(`${API_BASE}/profile`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return await response.json()
}

/**
 * Update user profile
 */
export async function updateProfile(token, profileData) {
  const response = await fetch(`${API_BASE}/profile`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(profileData)
  })

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return await response.json()
}

/**
 * Generate a conversation title using LLM
 */
export async function generateConversationTitle(token, conversationId, firstMessage) {
  const response = await fetch(`${API_BASE}/conversations/${conversationId}/generate-title`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ message: firstMessage })
  })

  if (!response.ok) {
    // If endpoint doesn't exist, generate a simple title from the message
    if (response.status === 404 || response.status === 405) {
      // Fallback: use first 50 chars of the message as title
      const title = firstMessage.substring(0, 50).trim() + (firstMessage.length > 50 ? '...' : '')
      return { title }
    }
    const error = new Error(`HTTP ${response.status}`)
    error.status = response.status
    throw error
  }

  return await response.json()
}
